import React, { useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';

import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import moment from 'moment';
import LoadingSpinner from 'src/components/shared/loadingSpinner/loadingSpinner';
import { RTW_SYSTEM } from 'src/constants';
import associateProfileService from 'src/services/associateProfile/associateProfileService';
import type { AssociateProfilePlacement } from 'src/services/associateProfile/types';
import { useAppSelector } from 'src/store/store';
import type { DataFetchingState } from 'src/types';
import type { RestrictionListParentTask, RestrictionListTask } from 'src/utils/restrictionUtilsLegacy';

import EmDash from '../emdash';
import ExportActionMenu from '../placements/placementActions/exportActions';
import UpdateActionMenu from '../placements/placementActions/updateActions';
import PlacementInfo from '../placements/placementInfo';

type PageState = DataFetchingState<Array<AssociateProfilePlacement>>;

const DEFAULT_PAGE_STATE: PageState = {
  status: 'loading',
};

interface AssociateProfileHistoryProps {
  regionTasks: (RestrictionListTask | RestrictionListParentTask)[];
}

const AssociateProfileHistory = ({ regionTasks }: AssociateProfileHistoryProps) => {
  const { alias } = useParams();
  const { selectedSite } = useAppSelector((state) => state.sites);
  const [pageState, setPageState] = useState<PageState>(DEFAULT_PAGE_STATE);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchHistory = async () => {
      setPageState({ status: 'loading' });

      if (!alias || selectedSite.id === 0) {
        if (!abortController.signal.aborted) {
          setPageState({ status: 'error' });
        }
        return;
      }

      try {
        const { data: history } = await associateProfileService.getAssociateProfileHistory(alias, selectedSite.id, {
          signal: abortController.signal,
        });

        if (!abortController.signal.aborted) {
          setPageState({
            status: 'success',
            data: history,
          });
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error('Failed to fetch page data:', error);
          setPageState({ status: 'error' });
        }
      }
    };

    fetchHistory();
  }, [alias, selectedSite]);

  if (pageState.status === 'loading') {
    return <LoadingSpinner />;
  }

  if (pageState.status === 'error') {
    return <Text>Oops! Something went wrong.</Text>;
  }

  return (
    <Column spacing="400">
      <Text type="b500">Closed Placements ({pageState.data.length})</Text>
      {pageState.data.map((placement) => (
        <Box
          key={placement.accommodationId}
          spacingInset="400 450"
          maxWidth="750"
          type="fill"
          backgroundColor="highlight"
        >
          <Column spacing="400">
            <Row widths={['fit', 'fill']}>
              <Column spacing="200">
                <Row>
                  <Text type="h100" tag="span">
                    Case ID:&nbsp;
                    <Link type="secondary" href={`/placementDetails/${placement.accommodationId}`}>
                      {placement.accommodationType} | {placement.externalCaseId ?? <EmDash />}
                    </Link>
                  </Text>
                </Row>
                <Row spacing="200">
                  <Text color="secondary">
                    Last Updated: {moment(placement.updatedAt).format('MM/DD/YYYY [at] h:mma')} UTC •
                  </Text>
                  {placement.updatedBy === RTW_SYSTEM ? (
                    <Text color="secondary">{placement.updatedBy}</Text>
                  ) : (
                    <Link
                      href={`https://phonetool.amazon.com/users/${placement.updatedBy}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {placement.updatedBy}@
                    </Link>
                  )}
                </Row>
              </Column>
              <Row spacing="300" alignmentHorizontal="end">
                <ExportActionMenu placement={placement} />
                <UpdateActionMenu placement={placement} regionTasks={regionTasks} />
              </Row>
            </Row>
            <Row spacing="600">
              <PlacementInfo label="Injury" value={placement.injury} />
              <PlacementInfo label="Start Date" value={new Date(placement.startAt).toLocaleDateString()} />
              <PlacementInfo label="End Date" value={new Date(placement.endAt).toLocaleDateString()} />
              <PlacementInfo label="Closed on" value={new Date(placement.closedAt).toLocaleDateString()} />
              <PlacementInfo label="Job(s)" value={placement.allocatedJobShifts.length} />
            </Row>
          </Column>
        </Box>
      ))}
    </Column>
  );
};

export default AssociateProfileHistory;
